<template>
    <div class="domestic-ringgit-borrowing-statement-modal">
        <img src="@webapp/assets/info.webp"
            class="domestic-ringgit-borrowing-statement-modal__image"
            alt="Modal icon"
        >
        <div class="domestic-ringgit-borrowing-statement-modal__wrapper">
            <h1 class="domestic-ringgit-borrowing-statement-modal__title">
                {{ $t('jurisdictionRequirements.domesticRinggitBorrowing.modalTitle') }}
            </h1>
            <translation-string :translation="$t('jurisdictionRequirements.domesticRinggitBorrowing.modalSubtitle')"
                class="domestic-ringgit-borrowing-statement-modal__text"
            />
            <div class="options">
                <div v-for="(option,index) in options"
                    :key="index"
                    class="radio__wrapper"
                    @click="toggleOption(index)"
                >
                    <div class="radio">
                        <div
                            class="radio__circle"
                        />
                        <div v-if="option.checked"
                            class="radio__circle--fill"
                        />
                    </div>
                    <label class="radio__label"
                        :for="index"
                    >
                        <translation-string
                            class="malaysian-statement__message"
                            :translation="option.text"
                        />
                    </label>
                </div>
            </div>
            <translation-string :translation="disclaimerText"
                class="domestic-ringgit-borrowing-statement-modal__text"
            />
        </div>

        <loading-spinner v-if="loading" />
        <div v-else
            class="domestic-ringgit-borrowing-statement-modal__actions"
        >
            <button class="button"
                :disabled="buttonDisabled"
                @click="onAcceptWrapper"
            >
                {{ $t('global.button.confirm') }}
            </button>
        </div>
    </div>
</template>

<script>
import {
    mapActions,
    mapGetters,
} from 'vuex';

// Components
import translationString from '@webapp/components/translation-string.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

// Utils
import {
    getUserAgreementLink,
} from '@shared/utils/user-agreement';

// Local Modules
import localStoreTypes from '@webapp/store/types.js';

// Socios variables
import socios from '/socios.json';

export default {
    name: 'malaysian-domestic-ringgit-borrowing-modal',
    components: {
        translationString,
        loadingSpinner,
    },
    props: {
        requirement: {
            type: Object,
            default: null,
        },
        routeToRedirect: {
            type: String,
            default: null,
        },
        onSuccess: {
            type: Function,
            default: () => ({}),
        },
        onCancel: {
            type: Function,
            default: () => ({}),
        },
    },
    data() {
        return {
            loading: false,
            termsAndConditions: null,
            options: [
                {
                    checked: false,
                    text: this.$t('jurisdictionRequirements.domesticRinggitBorrowing.modalOption1'),
                    value: false,
                },
                {
                    checked: false,
                    text: this.$t('jurisdictionRequirements.domesticRinggitBorrowing.modalOption2'),
                    value: true,
                },
            ],
        };
    },
    computed: {
        ...mapGetters('agreements', ['acceptedUserAgreement']),

        buttonDisabled() {
            return !this.options.some((option) => option.checked);
        },

        selectedOptionIndex() {
            return this.options.findIndex((option) => option.checked);
        },

        disclaimerText() {
            return this.$t('jurisdictionRequirements.domesticRinggitBorrowing.modalDescription', {
                termsAndConditionsLink: this.termsAndConditions,
            });
        },
    },
    watch: {
        acceptedUserAgreement: {
            immediate: true,
            handler(newValue) {
                this.termsAndConditions = newValue.link ? getUserAgreementLink(newValue) : `${socios.cmsUrl}/${this.localeWithUnderscore}/terms-and-conditions`;
            },
        },
    },
    mounted() {
        this.fetchAcceptedUserAgreement({
            details: ['agreementVersionTranslations'],
            locales: [this.localeISO2],
        });
    },
    methods: {
        ...mapActions({
            resetModal: localStoreTypes.RESET_MODAL,
        }),

        ...mapActions('agreements', ['fetchAcceptedUserAgreement']),

        updateOptionsByIndex(index) {
            this.options.forEach((opt) => {
                opt.checked = false;
            });

            this.options[index].checked = true;
        },

        toggleOption(index) {
            this.updateOptionsByIndex(index);
        },

        handleOptionChanged(index) {
            this.confirmationOptions[index].checked = !this.confirmationOptions[index].checked;
        },

        onAcceptWrapper() {
            this.$router.push({
                name: 'requirement',
                query: {
                    type: this.requirement.type,
                },
                params: {
                    componentToLoad: 'malaysian-statement',
                    componentProps: {
                        doesUserHaveDomesticRinggitBorrowing: this.selectedOptionIndex !== 0,
                        requirement: this.requirement,
                    },
                    requirement: this.requirement,
                    routeToRedirect: this.routeToRedirect,
                    onSuccess: this.onSuccess,
                    onCancel: this.onCancel,
                },
            });
            this.resetModal();
        },
    },
};
</script>

<style lang="scss" scoped>
.domestic-ringgit-borrowing-statement-modal {
    &__wrapper {
        padding-top: 2.6rem;
        background: $white-color;
        text-align: center;
        color: $cyprus-color;
        font-size: 1.5rem;
        line-height: 2rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;

        @include max-width(sm) {
            padding: 2rem 0;
        }
    }

    &__image {
        display: block;
        margin: 0 auto;
        object-fit: contain;
        max-width: 13.6rem;
        min-height: 12rem;

        @include min-width(md) {
            max-width: 15.6rem;
            min-height: 16rem;
        }

        @include max-width(sm) {
            max-width: 9.6rem;
            min-height: 11rem;
        }
    }

    &__title {
        font-size: 1.6rem;
        font-weight: bold;
    }

    &__text {
        font-size: 1.4rem;
        text-align: center;
        color: $cyprus-color;
    }

    .options {
        & .radio {
            position: relative;

            &__wrapper {
                display: flex;
                margin: 20px 0;
                cursor: pointer;
            }

            &__circle {
                height: 15px;
                width: 15px;
                margin-right: 10px;
                border: 1px solid $lima-color;
                border-radius: 50%;

                &--fill {
                    height: 11px;
                    width: 11px;
                    background-color: $lima-color;
                    border-radius: 50%;
                    position: absolute;
                    top: 3px;
                    left: 3px;
                }
            }

            &__label {
                font-size: 1.65rem;
                margin-top: -2px;
                cursor: pointer;
            }
        }
    }

    &__actions {
        padding: 1rem 0;
        display: block;
        width: 100%;
        margin-top: 2rem;

        .button {
            display: block;
            margin: 0 auto 1rem;
            height: 3.5rem;
            line-height: 3.5rem;
            width: 20rem;
            border-radius: 0.5rem;
            background: $lima-color;
            text-align: center;
            font-size: 1.4rem;
            color: $daintree-color;
            border: none;
            align-self: flex-end;

            @include min-width(md) {
                width: 21.9rem;
            }

            &:hover {
                cursor: pointer;
                opacity: 0.8;
            }

            &--disabled {
                opacity: 0.3;
                cursor: default;
            }
        }
    }
}
</style>